import {
  calculateEffortRate,
  haveValue,
  getCustomIncomeDetails,
  convertCurrencyFormat,
  getUserDisplayName,
  resolveIncomeSelection,
} from "../helper/common";
import { getSavedIncomeComponents } from "./payslipSelection";

// effort rate type of a shared profile: "tenant" | "guarantor" | "both"
export const getEffortRateType = (profile, personInfo = null) =>
  profile?.effort_rate_data?.tenant?.effort_rate_type ??
  (personInfo ?? profile?.tenantid)?.effort_rate_type ??
  "tenant";

/**
 * Single place that combines a tenant's and their guarantors' income the way
 * every page should: the tenant counts for effort type "tenant" / "both",
 * guarantors count for "guarantor" / "both".
 * - personInfo: the tenant (profile.tenantid or an application participant)
 * - guarantorIds: only these guarantors (selection in a list), null = all
 * Returns the per-person income objects too, for pages that show them.
 */
export const getProfileIncomeBreakdown = (
  personInfo,
  profile,
  { user, _t, deaWorkspace, guarantorIds = null, customIncome } = {}
) => {
  const personId = personInfo?._id;
  const incomeSelection = resolveIncomeSelection(
    profile?.income_selection,
    personId
  );
  const guarantorSources =
    incomeSelection?.[personId]?.guarantor_sources ??
    incomeSelection?.guarantor_sources;
  const customIncomeData =
    customIncome === undefined ? profile?.custom_income_sources : customIncome;
  const effortRateType = getEffortRateType(profile, personInfo);

  const tenantIncome = getCustomIncomeDetails(
    personInfo,
    user,
    _t,
    incomeSelection?.tenant_source,
    undefined,
    true,
    deaWorkspace,
    customIncomeData,
    getSavedIncomeComponents(profile?.income_selection, { tenantId: personId })
  );

  let guarantors = Array.isArray(personInfo?.gurantor_id)
    ? personInfo.gurantor_id.filter((g) => haveValue(g))
    : [];
  if (Array.isArray(guarantorIds)) {
    guarantors = guarantors.filter((g) => guarantorIds.includes(g?._id));
  }
  const guarantorIncomes = guarantors.map((guarantor) => ({
    guarantor,
    income: getCustomIncomeDetails(
      guarantor,
      user,
      _t,
      guarantorSources?.[guarantor?._id],
      undefined,
      true,
      deaWorkspace,
      customIncomeData,
      getSavedIncomeComponents(profile?.income_selection, {
        tenantId: personId,
        userId: guarantor?._id,
        isGuarantor: true,
      })
    ),
  }));

  const countTenant = ["tenant", "both"].includes(effortRateType);
  const countGuarantors = ["guarantor", "both"].includes(effortRateType);
  const tenantAmount = countTenant ? +(tenantIncome?.amount || 0) : 0;
  const guarantorAmount = countGuarantors
    ? guarantorIncomes.reduce((sum, g) => sum + +(g.income?.amount || 0), 0)
    : 0;

  return {
    effortRateType,
    countTenant,
    countGuarantors,
    tenantIncome,
    guarantorIncomes,
    tenantAmount,
    guarantorAmount,
    combinedIncome: tenantAmount + guarantorAmount,
  };
};

export const calculateParticipantRentAmount = (
  participantInfo,
  profile,
  user,
  _t,
  deaWorkspace
) => {
  try {
    if (!participantInfo) {
      return {
        combinedIncome: 0,
        effort_rate: 0,
        profile_effort_rate_percentage: 0,
      };
    }

    let profile_effort_rate_percentage =
      profile?.effort_rate_data?.tenant?.effort_rate_percentage;
    if (!haveValue(profile_effort_rate_percentage)) {
      profile_effort_rate_percentage =
        user?.current_workspace?.default_effort_rate ?? 35;
    }

    const tInfo = participantInfo;
    const { combinedIncome } = getProfileIncomeBreakdown(tInfo, profile, {
      user,
      _t,
      deaWorkspace,
    });

    const data = {
      ...profile,
      tenantid: tInfo,
      rent_percentage: profile_effort_rate_percentage,
    };
    let temp_obj = calculateEffortRate(combinedIncome, user, data);

    return {
      combinedIncome,
      effort_rate: !Number.isNaN(temp_obj?.effort_amount)
        ? Math.round(Number(temp_obj.effort_amount))
        : 0,
      profile_effort_rate_percentage,
    };
  } catch (error) {
    console.error("Error in calculateParticipantRentAmount:", error);
    return {
      combinedIncome: 0,
      effort_rate: 0,
      profile_effort_rate_percentage: 0,
    };
  }
};

export const aggregateApplicationData = (
  profile,
  user,
  _t,
  deaWorkspace,
  checkSalary
) => {
  try {
    if (!profile?.application_id) {
      return {
        tenantName: profile?.tenantid ? "Unknown Application" : "Unknown",
        totalScore: 0,
        totalIncome: 0,
        totalEffortRate: 0,
        totalFlatmates: 0,
        totalGuarantors: 0,
        linkedProperties: profile?.propertyid || [],
        latestUpdateDate: profile?.sortDate,
      };
    }

    const acceptedParticipants =
      profile?.application_id?.participants?.filter(
        (p) => !p.is_deleted && p.consent_status === "accepted" && p.tenant_info
      ) || [];

    if (acceptedParticipants.length === 0) {
      const owner = profile?.application_id?.creator_deleted
        ? null
        : profile?.application_id?.participants?.find(
            (p) => p.id === profile?.application_id?.created_by
          );
      const ownerName = owner?.tenant_info
        ? getUserDisplayName(owner?.tenant_info)
        : "";
      return {
        tenantName: ownerName
          ? ownerName
          : profile?.application_id?.title || "Unknown Application",
        totalScore: 0,
        totalIncome: 0,
        totalEffortRate: 0,
        totalFlatmates: 0,
        totalGuarantors: 0,
        linkedProperties: profile?.propertyid || [],
        latestUpdateDate: profile?.sortDate,
      };
    }

    let totalScore = 0;
    let totalIncome = 0;
    let totalEffortRate = 0;
    let totalFlatmates = 0;
    let totalGuarantors = 0;
    let latestUpdateDate = null;

    acceptedParticipants.forEach((participant) => {
      const tenantInfo = participant.tenant_info;

      if (checkSalary && typeof checkSalary === "function" && tenantInfo) {
        try {
          const salaryResult = checkSalary(tenantInfo);
          if (salaryResult) {
            totalScore += salaryResult.matched || 0;
          }
        } catch (error) {
          console.error("Error checking salary:", error);
        }
      }

      try {
        const rentAmount = calculateParticipantRentAmount(
          tenantInfo,
          { ...participant, ...profile },
          user,
          _t,
          deaWorkspace
        );
        totalIncome += rentAmount.combinedIncome || 0;
        totalEffortRate += rentAmount.effort_rate || 0;
      } catch (error) {
        console.error("Error calculating rent amount:", error);
      }

      totalFlatmates += tenantInfo?.sharers?.length || 0;
      totalGuarantors += (tenantInfo?.gurantor_id || []).length;

      const participantUpdateDate = tenantInfo?.sortDate;
      if (participantUpdateDate) {
        if (
          !latestUpdateDate ||
          new Date(participantUpdateDate) > new Date(latestUpdateDate)
        ) {
          latestUpdateDate = participantUpdateDate;
        }
      }
    });

    if (!latestUpdateDate) {
      latestUpdateDate = profile?.sortDate;
    }
    const owner = profile?.application_id?.creator_deleted
      ? null
      : profile?.application_id?.participants?.find(
          (p) => p.id === profile?.application_id?.created_by
        );
    const ownerName = owner?.tenant_info
      ? getUserDisplayName(owner?.tenant_info)
      : "";

    return {
      tenantName: ownerName
        ? ownerName
        : profile?.application_id?.title || "Unknown Application",
      totalScore,
      totalIncome,
      totalEffortRate,
      totalFlatmates,
      totalGuarantors,
      linkedProperties: profile?.propertyid || [],
      latestUpdateDate,
    };
  } catch (error) {
    console.error("Error in aggregateApplicationData:", error);
    return {
      tenantName: "Error Loading Application",
      totalScore: 0,
      totalIncome: 0,
      totalEffortRate: 0,
      totalFlatmates: 0,
      totalGuarantors: 0,
      linkedProperties: [],
      latestUpdateDate: null,
    };
  }
};
